import { getProductivityDetails, getRetentionDetails } from '../calculations';
import { CHART_COLORS } from '../constants';
import { formatCurrency, formatNumber } from '../formatters';
import { type CurrencyConfig, type CurrencyKey, type RoiResults, type SliderInput } from '../types';
import DataTable, { type DataRow } from './DataTable';
import DonutChart from './DonutChart';
import HorizontalBarChart from './HorizontalBarChart';
import styles from './Report.module.scss';

interface RetentionSectionProps {
  results: RoiResults;
  inputs: SliderInput;
  currencyConfig: CurrencyConfig;
  currency: CurrencyKey;
}

export default function RetentionSection({
  results,
  inputs,
  currencyConfig,
  currency,
}: RetentionSectionProps) {
  const productivity = getProductivityDetails(inputs, currencyConfig);
  const details = getRetentionDetails(inputs, productivity);

  const yearsData = [
    { name: 'Year 1', value: results.retention.total.year1, color: CHART_COLORS.plum },
    { name: 'Year 2', value: results.retention.total.year2, color: CHART_COLORS.plumLight },
    { name: 'Year 3', value: results.retention.total.year3, color: CHART_COLORS.orangeDark },
  ];

  const splitData = [
    { name: 'Desktop Workers', value: results.retention.desktop.total, color: CHART_COLORS.plum },
    {
      name: 'Frontline Workers',
      value: results.retention.frontline.total,
      color: CHART_COLORS.orangeDark,
    },
  ];

  const desktopRows: DataRow[] = [
    {
      label: 'Average salary',
      calculation: 'Includes an average of 3% rise per year',
      values: [...details.desktop.averageSalary.map((v) => formatCurrency(v, currency)), ''],
    },
    {
      label: 'Churn per year',
      calculation: 'Based on estimated yearly churn rate',
      values: [...details.desktop.churnPerYear.map(formatNumber), ''],
    },
    {
      label: 'Actual cost of replacing employee',
      calculation: '20% of average salary',
      values: ['20%', '20%', '20%', ''],
    },
    {
      label: 'Total cost of replacing employees',
      calculation: '(Salary * 20%) * churn count',
      values: [...details.desktop.totalReplacementCost.map((v) => formatCurrency(v, currency)), ''],
    },
    {
      label: "Modern intranet's impact on churn",
      calculation: '0.3% reduction in churn rate',
      values: ['0.3%', '0.3%', '0.3%', ''],
    },
    {
      label: 'Reduced cost of replacing employees',
      calculation: 'Cost with reduced churn rate',
      values: [
        ...details.desktop.reducedReplacementCost.map((v) => formatCurrency(v, currency)),
        '',
      ],
    },
    {
      label: 'Approximate saving',
      calculation: 'Total cost - reduced cost',
      values: [...details.desktop.approximateSaving.map((v) => formatCurrency(v, currency)), ''],
    },
    {
      label: 'Savings for employee churn',
      calculation: 'Adjusted for unforeseeable business change (35%)',
      values: [
        ...details.desktop.savingsForChurn.map((v) => formatCurrency(v, currency)),
        formatCurrency(details.desktop.savingsTotal, currency),
      ],
    },
  ];

  const frontlineRows: DataRow[] = [
    {
      label: 'Average salary',
      calculation: 'Includes an average of 3% rise per year',
      values: [...details.frontline.averageSalary.map((v) => formatCurrency(v, currency)), ''],
    },
    {
      label: 'Churn per year',
      calculation: 'Based on estimated yearly churn rate',
      values: [...details.frontline.churnPerYear.map(formatNumber), ''],
    },
    {
      label: 'Actual cost of replacing employee',
      calculation: '15% of average salary',
      values: ['15%', '15%', '15%', ''],
    },
    {
      label: 'Total cost of replacing employees',
      calculation: '(Salary * 15%) * churn count',
      values: [
        ...details.frontline.totalReplacementCost.map((v) => formatCurrency(v, currency)),
        '',
      ],
    },
    {
      label: "Modern intranet's impact on churn",
      calculation: '0.1% reduction in churn rate',
      values: ['0.1%', '0.1%', '0.1%', ''],
    },
    {
      label: 'Reduced cost of replacing employees',
      calculation: 'Cost with reduced churn rate',
      values: [
        ...details.frontline.reducedReplacementCost.map((v) => formatCurrency(v, currency)),
        '',
      ],
    },
    {
      label: 'Approximate saving',
      calculation: 'Total cost - reduced cost',
      values: [...details.frontline.approximateSaving.map((v) => formatCurrency(v, currency)), ''],
    },
    {
      label: 'Savings for employee churn',
      calculation: 'Adjusted for unforeseeable business change (35%)',
      values: [
        ...details.frontline.savingsForChurn.map((v) => formatCurrency(v, currency)),
        formatCurrency(details.frontline.savingsTotal, currency),
      ],
    },
  ];

  const tableHeaders = ['Retention', 'Calculation', 'Year 1', 'Year 2', 'Year 3', 'Total'];

  return (
    <section className={styles.section} aria-label="Retention Returns">
      <div className={styles.sectionInner}>
        <div className={styles.splitLayout}>
          <div>
            <h2 className={styles.sectionTitle}>Retention Returns</h2>
            <p className={styles.bodyText}>
              The financial and knowledge-costs of losing and replacing dissatisfied employees are
              substantial. They can lead to higher training costs, lower productivity, and low
              employee engagement.
            </p>
            <p className={styles.bodyText}>
              Every leadership team at every organization understands that higher than average churn
              rates will negatively impact their bottom line. Implementing a modern intranet is one
              of the options available to organizations seeking to make improvements to their
              employee retention rate.
            </p>
            <p className={styles.bodyText}>
              The reason why intranet platforms can reduce employee attrition lies in their capacity
              to improve experience and engagement. It is widely understood that employee engagement
              has a positive impact on retention because lower engagement, demonstrated by an
              employee&apos;s unwillingness to voluntarily participate in organizational
              initiatives, can raise absenteeism, lower productivity, and reduce profitability. It
              has been suggested that{' '}
              <a
                href="https://www.linkedin.com/business/learning/blog/learner-engagement/how-to-calculate-the-cost-of-employee-disengagement"
                target="_blank"
                rel="noopener noreferrer"
              >
                actively disengaged employees
              </a>{' '}
              cost organizations 34% of their salaries.
            </p>
            <p className={styles.bodyText}>
              A modern intranet is a vital tool for improving employee engagement. Improved one- and
              two-way communications using multichannel messaging for maximum reach helps
              communicators reach every employee and boost engagement levels.
            </p>
          </div>
          <div>
            <span className={styles.eyebrow}>Over three years</span>
            <h3 className={styles.sectionValue}>
              {formatCurrency(results.retention.total.total, currency)}
            </h3>
            <HorizontalBarChart data={yearsData} currency={currency} height={180} />
            <div className={styles.chartSpacer} />
            <DonutChart data={splitData} currency={currency} />
          </div>
        </div>

        <DataTable
          title="Desktop workers"
          headerIcon="desktop"
          headers={tableHeaders}
          rows={desktopRows}
        />
        <div className={styles.tableSpacer} />
        <DataTable
          title="Frontline workers"
          headerIcon="frontline"
          headers={tableHeaders}
          rows={frontlineRows}
        />
      </div>
    </section>
  );
}
